import { useEffect, useRef, useState } from "react";
import "./AuthForms.css";
import { supabase } from "./supabaseClient";
import { useNavigate } from "react-router-dom";

export default function ResetPassword() {
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [accountEmail, setAccountEmail] = useState(null);
  const [accountChecked, setAccountChecked] = useState(false);
  const navigate = useNavigate();

  const submitting = useRef(false);

  useEffect(() => {
    let active = true;
    supabase.auth.getUser()
      .then(({ data, error }) => {
        if (active) setAccountEmail(error ? null : data?.user?.email || null);
      })
      .catch(() => {
        if (active) setAccountEmail(null);
      })
      .finally(() => {
        if (active) setAccountChecked(true);
      });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => navigate("/dashboard"), 2000);
    return () => clearTimeout(timer);
  }, [message, navigate]);

  const handleReset = async (event) => {
    event.preventDefault();
    if (submitting.current) return;
    setError(null);
    if (password !== confirm) { setError("Passwords do not match."); return; }
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    submitting.current = true;
    setLoading(true);
    let succeeded = false;
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) setError(error.message);
      else {
        succeeded = true;
        setMessage("Password updated successfully!");
      }
    } catch {
      setError("We could not confirm the password update. Check your connection and try again.");
    } finally {
      submitting.current = succeeded;
      setLoading(false);
    }
  };

  return (
    <div className="vq-auth" style={S.root}>
      <div style={S.bg} />
      <form className="vq-auth-card" style={S.card} onSubmit={handleReset}>
        <div style={S.logo}>Vet<span style={{ color: "#fff" }}>Qualified</span></div>
        <h1 style={S.heading}>Set new password</h1>
        <p style={S.sub}>Choose a strong password for your VetQualified account.</p>
        {accountEmail ? (
          <div style={S.field}>
            <label htmlFor="reset-username" style={S.label}>Account email</label>
            <input id="reset-username" name="username" type="email" autoComplete="username" readOnly value={accountEmail} style={S.input} />
          </div>
        ) : (
          <p role="status" style={S.sub}>
            {accountChecked
              ? "We couldn't confirm the account email. Check the recipient of your reset email before continuing."
              : "Checking the account for this reset link…"}
          </p>
        )}

        <div style={S.field}>
          <label htmlFor="reset-password" style={S.label}>New Password</label>
          <input id="reset-password" name="new-password" autoComplete="new-password" disabled={loading || !!message} style={S.input} type="password" placeholder="••••••••" value={password} onChange={e => setPassword(e.target.value)} />
        </div>
        <div style={S.field}>
          <label htmlFor="reset-confirm" style={S.label}>Confirm New Password</label>
          <input id="reset-confirm" name="confirm-password" autoComplete="new-password" disabled={loading || !!message} style={S.input} type="password" placeholder="••••••••" value={confirm} onChange={e => setConfirm(e.target.value)} />
        </div>

        {error && <div role="alert" style={S.error}>{error}</div>}
        {message && <div role="status" style={S.success}>{message}</div>}

        <button type="submit" style={{ ...S.btn, ...(loading ? S.btnLoading : {}) }} disabled={loading || !!message}>
          {loading ? "Updating…" : "Update Password"}
        </button>
      </form>
    </div>
  );
}

const S = {
  root: { fontFamily: "'DM Sans', sans-serif", background: "#0B1F3A", minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", position: "relative" },
  bg: { position: "fixed", inset: 0, background: "radial-gradient(ellipse 70% 50% at 15% 20%, rgba(201,168,76,0.07) 0%, transparent 60%)", pointerEvents: "none", zIndex: 0 },
  card: { position: "relative", zIndex: 1, background: "rgba(255,255,255,0.04)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 16, padding: "2.5rem", width: "100%", maxWidth: 420 },
  logo: { fontFamily: "'DM Serif Display', serif", fontSize: "1.5rem", color: "#C9A84C", marginBottom: "1.5rem" },
  heading: { fontFamily: "'DM Serif Display', serif", fontSize: "1.75rem", color: "#fff", marginBottom: "0.5rem" },
  sub: { fontSize: "0.85rem", color: "#8FA3BE", marginBottom: "1.75rem", lineHeight: 1.6 },
  field: { marginBottom: "1rem" },
  label: { display: "block", fontSize: "0.65rem", fontWeight: 700, letterSpacing: "0.12em", textTransform: "uppercase", color: "#8FA3BE", marginBottom: "0.4rem" },
  input: { width: "100%", background: "rgba(255,255,255,0.06)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 8, color: "#fff", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", padding: "0.7rem 0.9rem", boxSizing: "border-box" },
  error: { fontSize: "0.78rem", color: "#f87171", background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  success: { fontSize: "0.78rem", color: "#4ade80", background: "rgba(74,222,128,0.1)", border: "1px solid rgba(74,222,128,0.2)", borderRadius: 8, padding: "0.6rem 0.9rem", marginBottom: "1rem" },
  btn: { width: "100%", padding: "0.8rem", background: "#C9A84C", border: "none", borderRadius: 8, color: "#0B1F3A", fontFamily: "'DM Sans', sans-serif", fontSize: "0.9rem", fontWeight: 800, cursor: "pointer", letterSpacing: "0.04em" },
  btnLoading: { background: "#A88830", cursor: "not-allowed" },
};